/* eslint eqeqeq: "off" */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button, Modal, Form, Spinner } from "react-bootstrap";
import DatePicker from "react-datepicker";
import moment from "moment";

/**
 * Pause / Resume / Extend a batch enrollment via /api/enrollments.
 * mode: "pause" | "resume" | "extend"
 */
export function PauseEnrollmentDialog({
  show,
  setShow,
  mode,
  course,
  studentName,
  setRefetch,
  reloadStudent,
}) {
  const today = moment().format("YYYY-MM-DD");
  const [pauseStart, setPauseStart] = useState(today);
  const [pauseEnd, setPauseEnd] = useState("");
  const [reasonId, setReasonId] = useState("");
  const [reasons, setReasons] = useState([]);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const studentBatchId = course && course.student_batch_id;

  // The current pause's end date lives on the loaded batch (family-view exposes
  // pause_end for the open pause). Used to pre-fill / floor the Extend input.
  // The open pause's dates come from whichever batch carries them: family-view
  // exposes pause_start/pause_end on both a started (active_id 6) and an upcoming
  // (active_id 1) batch, and the dashboard passes a single adapted batch. Used to
  // pre-fill / floor the Extend and Resume date pickers — so Extend works (and is
  // floored at the real return date) even for a not-yet-started pause.
  const currentPauseEnd =
    (course &&
      course.batches &&
      (course.batches.find((b) => b.pause_end) || {}).pause_end) ||
    "";
  const currentPauseStart =
    (course &&
      course.batches &&
      (course.batches.find((b) => b.pause_start) || {}).pause_start) ||
    "";

  // The break has actually begun only when a batch is Paused (active_id 6). This
  // gates Resume: a real return (date picker) vs. cancelling an upcoming pause.
  const pauseStarted = !!(
    course &&
    course.batches &&
    course.batches.find((b) => b.active_id == 6)
  );

  // Pauses can only start / end on a day the batch actually meets. batch.day
  // is a day name or a comma list ("Friday" / "Tuesday,Thursday"), so build the
  // set of allowed weekday indices (0=Sun..6=Sat) and only enable those in the
  // date pickers.
  const batchDayNames = useMemo(() => {
    const raw = (course && course.batches ? course.batches : [])
      .map((b) => b.day)
      .filter(Boolean)
      .join(",")
      .split(",")
      .map((d) => d.trim())
      .filter(Boolean);
    return Array.from(new Set(raw));
  }, [course]);

  const allowedDayIdx = useMemo(() => {
    return new Set(
      batchDayNames.map((d) => moment(d, "dddd").day()).filter((n) => !isNaN(n))
    );
  }, [batchDayNames]);

  // Accepts a Date (react-datepicker filterDate) or a YYYY-MM-DD string.
  const isBatchDay = (date) =>
    allowedDayIdx.size === 0 || allowedDayIdx.has(moment(date).day());

  // First date on/after `fromStr` that lands on a batch day (used to seed the
  // pause-start default so it opens on a valid class day).
  const nextBatchDay = (fromStr) => {
    let d = moment(fromStr, "YYYY-MM-DD");
    for (let i = 0; i < 7; i++) {
      if (isBatchDay(d.toDate())) return d.format("YYYY-MM-DD");
      d = d.add(1, "day");
    }
    return fromStr;
  };

  const classDayMessage = `Pause dates must be on the class day${
    batchDayNames.length > 1 ? "s" : ""
  }: ${batchDayNames.join(", ")}.`;

  const toDate = (str) => (str ? moment(str, "YYYY-MM-DD").toDate() : null);

  useEffect(() => {
    if (show && mode === "pause") {
      axios("api/enrollments/pause-reasons")
        .then((res) => setReasons(res.data.results || []))
        .catch(() => setReasons([]));
      // Open on the next valid class day rather than today (which may be disabled).
      setPauseStart(nextBatchDay(today));
      setPauseEnd("");
    }
    if (show && mode === "extend") {
      setPauseEnd(currentPauseEnd ? currentPauseEnd.split("T")[0] : "");
    }
    if (show && mode === "resume") {
      // Default to the next class day (the usual "resume now"); a started pause
      // can be backdated down to pause_start via the picker.
      setPauseEnd(pauseStarted ? nextBatchDay(today) : "");
    }
    if (show) {
      setError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, mode]);

  const close = () => {
    setShow(false);
    setError("");
  };

  const done = () => {
    setSaving(false);
    setShow(false);
    setRefetch(true);
    // Reload the parent student's course data so the pause badge/status and
    // current-vs-past placement update without a manual page refresh.
    reloadStudent && reloadStudent(true);
  };

  const handleError = (err) => {
    setSaving(false);
    setError(
      (err.response && err.response.data && err.response.data.message) ||
        "Something went wrong. Please try again."
    );
  };

  const submitPause = async () => {
    if (!pauseEnd) return setError("Please choose a return date.");
    if (!reasonId) return setError("Please choose a reason.");
    if (moment(pauseEnd).isBefore(pauseStart))
      return setError("Return date must be on or after the pause start.");
    if (!isBatchDay(pauseStart) || !isBatchDay(pauseEnd))
      return setError(classDayMessage);
    setSaving(true);
    try {
      await axios.post(`api/enrollments/${studentBatchId}/pause`, {
        pause_start: pauseStart,
        pause_end: pauseEnd,
        pause_reason_id: Number(reasonId),
        note: note || null,
      });
      done();
    } catch (err) {
      handleError(err);
    }
  };

  const submitExtend = async () => {
    if (!pauseEnd) return setError("Please choose a new return date.");
    if (!isBatchDay(pauseEnd)) return setError(classDayMessage);
    setSaving(true);
    try {
      await axios.post(`api/enrollments/${studentBatchId}/extend`, {
        pause_end: pauseEnd,
        note: note || null,
      });
      done();
    } catch (err) {
      handleError(err);
    }
  };

  const submitResume = async () => {
    // A started pause records the ACTUAL return date (backdatable to pause_start);
    // an upcoming (not-yet-started) pause is just cancelled — no date.
    if (pauseStarted) {
      if (!pauseEnd) return setError("Please choose a return date.");
      if (!isBatchDay(pauseEnd)) return setError(classDayMessage);
      if (
        currentPauseStart &&
        moment(pauseEnd).isBefore(
          moment(currentPauseStart.split("T")[0], "YYYY-MM-DD")
        )
      )
        return setError("Return date must be on or after the pause start.");
    }
    setSaving(true);
    try {
      await axios.post(
        `api/enrollments/${studentBatchId}/resume`,
        pauseStarted ? { pause_end: pauseEnd } : {}
      );
      done();
    } catch (err) {
      handleError(err);
    }
  };

  const title =
    mode === "pause"
      ? "Pause Enrollment"
      : mode === "extend"
      ? "Extend Pause"
      : "Resume Enrollment";

  return (
    <Modal show={show} onHide={close}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error ? <div className="alert alert-danger">{error}</div> : ""}

        {mode === "pause" && (
          <Form>
            {batchDayNames.length > 0 && (
              <div className="text-muted mb-2">
                Pauses start and end on the class day
                {batchDayNames.length > 1 ? "s" : ""}:{" "}
                <b>{batchDayNames.join(", ")}</b>.
              </div>
            )}
            <Form.Group>
              <Form.Label>Pause start</Form.Label>
              <DatePicker
                className="form-control"
                wrapperClassName="d-block w-100"
                selected={toDate(pauseStart)}
                onChange={(val) =>
                  setPauseStart(val ? moment(val).format("YYYY-MM-DD") : "")
                }
                filterDate={isBatchDay}
                dateFormat="yyyy-MM-dd"
                placeholderText="Select a class day"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Return date (required)</Form.Label>
              <DatePicker
                className="form-control"
                wrapperClassName="d-block w-100"
                selected={toDate(pauseEnd)}
                onChange={(val) =>
                  setPauseEnd(val ? moment(val).format("YYYY-MM-DD") : "")
                }
                filterDate={isBatchDay}
                minDate={toDate(pauseStart)}
                dateFormat="yyyy-MM-dd"
                placeholderText="Select a class day"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Reason</Form.Label>
              <Form.Control
                as="select"
                value={reasonId}
                onChange={(e) => setReasonId(e.target.value)}
              >
                <option value="">Select a reason…</option>
                {reasons.map((r) => (
                  <option key={r.pause_reason_id} value={r.pause_reason_id}>
                    {r.reason_value}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            <Form.Group>
              <Form.Label>Note (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </Form.Group>
            {pauseEnd && isBatchDay(pauseEnd) && (
              <div className="alert alert-info py-2 mb-0">
                {studentName ? `${studentName} returns` : "Student returns"} to
                class on{" "}
                <b>{moment(pauseEnd, "YYYY-MM-DD").format("dddd, MMM D, YYYY")}</b>
                .
              </div>
            )}
          </Form>
        )}

        {mode === "extend" && (
          <Form>
            {batchDayNames.length > 0 && (
              <div className="text-muted mb-2">
                Return date must fall on the class day
                {batchDayNames.length > 1 ? "s" : ""}:{" "}
                <b>{batchDayNames.join(", ")}</b>.
              </div>
            )}
            <Form.Group>
              <Form.Label>New return date</Form.Label>
              <DatePicker
                className="form-control"
                wrapperClassName="d-block w-100"
                selected={toDate(pauseEnd)}
                onChange={(val) =>
                  setPauseEnd(val ? moment(val).format("YYYY-MM-DD") : "")
                }
                filterDate={isBatchDay}
                minDate={toDate(
                  currentPauseEnd ? currentPauseEnd.split("T")[0] : today
                )}
                dateFormat="yyyy-MM-dd"
                placeholderText="Select a class day"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Note (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </Form.Group>
            {pauseEnd && isBatchDay(pauseEnd) && (
              <div className="alert alert-info py-2 mb-0">
                {studentName ? `${studentName} returns` : "Student returns"} to
                class on{" "}
                <b>{moment(pauseEnd, "YYYY-MM-DD").format("dddd, MMM D, YYYY")}</b>
                .
              </div>
            )}
          </Form>
        )}

        {mode === "resume" &&
          (pauseStarted ? (
            <Form>
              {batchDayNames.length > 0 && (
                <div className="text-muted mb-2">
                  Return date must fall on the class day
                  {batchDayNames.length > 1 ? "s" : ""}:{" "}
                  <b>{batchDayNames.join(", ")}</b>.
                </div>
              )}
              <Form.Group>
                <Form.Label>Return date (the day they actually came back)</Form.Label>
                <DatePicker
                  className="form-control"
                  wrapperClassName="d-block w-100"
                  selected={toDate(pauseEnd)}
                  onChange={(val) =>
                    setPauseEnd(val ? moment(val).format("YYYY-MM-DD") : "")
                  }
                  filterDate={isBatchDay}
                  minDate={toDate(
                    currentPauseStart ? currentPauseStart.split("T")[0] : today
                  )}
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Select a class day"
                />
              </Form.Group>
              {pauseEnd && isBatchDay(pauseEnd) && (
                <div className="alert alert-info py-2 mb-0">
                  {studentName ? `${studentName} returns` : "Student returns"} to
                  class on{" "}
                  <b>
                    {moment(pauseEnd, "YYYY-MM-DD").format("dddd, MMM D, YYYY")}
                  </b>
                  . The break is recorded up to this date.
                </div>
              )}
            </Form>
          ) : (
            <div>
              Cancel this scheduled pause? The enrollment stays active in the same
              batch — no break will be recorded.
            </div>
          ))}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={close} disabled={saving}>
          Close
        </Button>
        <Button
          variant={mode === "resume" ? "success" : "primary"}
          disabled={saving}
          onClick={
            mode === "pause"
              ? submitPause
              : mode === "extend"
              ? submitExtend
              : submitResume
          }
        >
          {saving ? (
            <Spinner animation="border" size="sm" />
          ) : mode === "pause" ? (
            "Pause"
          ) : mode === "extend" ? (
            "Extend"
          ) : (
            "Resume"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
